import * as React from 'react';
import { Loader } from '@grandvisionhq/common';
import { Container, Heading } from '@grandvisionhq/elements';
import { actions as actions$1, reducer, useIntl } from '@grandvisionhq/state';
import { createScriptTag, injectTags } from '@grandvisionhq/utils';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.
Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.
THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}
function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || from);
}

/**
 * Maps our settings to an object that's accepted by PowerReviews.display.render() method
 */
var createPowerReviewsConfig = function (_a) {
    var _b;
    var config = _a.config, locale = _a.locale, productId = _a.productId, variantId = _a.variantId, type = _a.type, id = _a.id, returnUrl = _a.returnUrl, onRender = _a.onRender, onSubmit = _a.onSubmit;
    return (__assign(__assign(__assign(__assign(__assign({ enable_content_collection_modal: false, api_key: config.apiKey, locale: locale.replace('-', '_'), merchant_group_id: config.groupId, merchant_id: config.opcoId, 
        // Append the "?" as PowerReviews doesn't do that but will add query params.
        review_wrapper_url: config.writeReviewPath + "?", components: (_b = {},
            _b[type] = id,
            _b) }, (productId && { page_id: productId })), (variantId && { page_id_variant: variantId })), (returnUrl && { return_url: returnUrl })), (onRender && { on_render: onRender })), (onSubmit && { on_submit: onSubmit })));
};
/**
 * PowerReviews doesn't accept multiple widgets to be loaded separately.
 * It always expects one object instead of an array or multiple calls to the render fn.
 */
var mergeConfigs = function (configs) {
    return configs.reduce(function (result, next) { return (__assign(__assign(__assign({}, result), next), { components: __assign(__assign({}, result.components), next.components) })); }, {});
};

var loadReviewWrite = actions$1.action('LOAD_REVIEW_WRITE', actions$1.payload());
var loadReviewSnippet = actions$1.action('LOAD_REVIEW_SNIPPET', actions$1.payload());
var loadReviewDisplay = actions$1.action('LOAD_REVIEW_DISPLAY', actions$1.payload());
var updateReviewDisplay = actions$1.action('UPDATE_REVIEW_DISPLAY', actions$1.payload());

var actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    loadReviewWrite: loadReviewWrite,
    loadReviewSnippet: loadReviewSnippet,
    loadReviewDisplay: loadReviewDisplay,
    updateReviewDisplay: updateReviewDisplay
});

var initialState = {
    reviewWrite: null,
    reviewSnippet: null,
    reviewDisplay: null,
};
var reviewsReducer = reducer(initialState, 
/**
 * Load widgets
 */
actions$1.on(loadReviewWrite, function (state, action) { return (__assign(__assign({}, state), { reviewWrite: action.payload })); }), actions$1.on(loadReviewSnippet, function (state, action) { return (__assign(__assign({}, state), { reviewSnippet: action.payload })); }), actions$1.on(loadReviewDisplay, function (state, action) { return (__assign(__assign({}, state), { reviewDisplay: action.payload })); }), 
/**
 * Update widgets
 */
actions$1.on(updateReviewDisplay, function (state, action) {
    return state.reviewDisplay !== null
        ? __assign(__assign({}, state), { reviewDisplay: __assign(__assign({}, state.reviewDisplay), { variantId: action.payload.variantId }) }) : state;
}));

// These are the keys PowerReviews requires in the `components` object when initializing a widget
var mapStateToPowerReviewsType = {
    reviewWrite: 'Write',
    reviewSnippet: 'ReviewSnippet',
    reviewDisplay: 'ReviewDisplay',
};
var ReviewsContext = React.createContext({});
var useReviewsContext = function () { return React.useContext(ReviewsContext); };
var PR_SCRIPT_ID = 'power-reviews-script';
var PR_SCRIPT_SRC = 'https://ui.powerreviews.com/stable/4.1/ui.js';
var ReviewsProvider = function (_a) {
    var enabled = _a.enabled, config = _a.config, children = _a.children;
    var _b = __read(React.useReducer(reviewsReducer, initialState), 2), state = _b[0], dispatch = _b[1];
    var locale = useIntl().locale;
    var widgets = React.useMemo(function () {
        return Object.keys(state).reduce(function (result, key) {
            if (state[key] !== null) {
                var widgetConfig = createPowerReviewsConfig(__assign(__assign({ config: config, locale: locale }, state[key]), { type: mapStateToPowerReviewsType[key] }));
                return __spreadArray(__spreadArray([], __read(result)), [widgetConfig]);
            }
            return result;
        }, []);
    }, [state]);
    React.useEffect(function () {
        var script = createScriptTag({
            id: PR_SCRIPT_ID,
            src: PR_SCRIPT_SRC,
            defer: true,
        });
        injectTags([script]);
    }, []);
    React.useEffect(function () {
        if (enabled && widgets.length) {
            var mergedConfig_1 = widgets.length > 1 ? mergeConfigs(widgets) : widgets[0];
            // Clear existing widgets and loaders before (re)initializing
            Object.values(mergedConfig_1.components).forEach(function (widgetId) {
                clearWidgetHtml(widgetId);
            });
            // Set powerreviews configuration. powerreviews has a complex async script loading and does not provide a proper load hook.
            // To prevent a race condition we prepare the config beforehand.
            // This is the official way powerreviews should be declared.
            window.pwr =
                window.pwr ||
                    function () {
                        var args = [];
                        for (var _i = 0; _i < arguments.length; _i++) {
                            args[_i] = arguments[_i];
                        }
                        (pwr.q = pwr.q || []).push(args);
                    };
            window.pwr('render', mergedConfig_1);
            // When new widgets are updated, first remove the old from powerreviews as they might have the same id and therefore will not be rendered.
            return function () {
                Object.values(mergedConfig_1.components).forEach(function (widgetId) {
                    window.pwr('unmount', widgetId);
                });
            };
        }
    }, [widgets, enabled]);
    var value = React.useMemo(function () { return ({
        enabled: enabled,
        config: config,
        dispatch: dispatch,
    }); }, [enabled, config]);
    return React.createElement(ReviewsContext.Provider, { value: value }, children);
};
// Because PowerReviews doesn't clear their widget's HTML themselves
// when we re-call their `display` method
function clearWidgetHtml(id) {
    var element = document.getElementById(id);
    if (element) {
        element.innerHTML = '';
    }
}

var REVIEW_DISPLAY_ID = 'pr-review-display';
var ReviewDisplay = function (_a) {
    var productId = _a.productId, variantId = _a.variantId;
    var _b = useReviewsContext(), enabled = _b.enabled, dispatch = _b.dispatch;
    React.useEffect(function () {
        if (enabled && productId) {
            dispatch(loadReviewDisplay({
                id: REVIEW_DISPLAY_ID,
                productId: productId,
                variantId: variantId,
            }));
        }
    }, [enabled, productId, variantId]);
    if (!enabled) {
        return null;
    }
    return (React.createElement("div", { className: "review-display", "data-t": "review-display" },
        React.createElement(Container, null,
            React.createElement("div", { id: REVIEW_DISPLAY_ID },
                React.createElement(Loader, null)))));
};

var REVIEW_SNIPPET_ID = 'pr-review-snippet';
var ReviewSnippet = function (_a) {
    var productId = _a.productId, variantId = _a.variantId;
    var _b = useReviewsContext(), enabled = _b.enabled, dispatch = _b.dispatch;
    React.useEffect(function () {
        if (enabled && productId) {
            dispatch(loadReviewSnippet({
                id: REVIEW_SNIPPET_ID,
                productId: productId,
                variantId: variantId,
            }));
        }
    }, [enabled, productId, variantId]);
    if (!enabled) {
        return null;
    }
    return (React.createElement("div", { className: "review-snippet", "data-t": "review-snippet" },
        React.createElement("div", { id: REVIEW_SNIPPET_ID })));
};

var REVIEW_WRITE_ID = 'pr-review-write';
var ReviewWrite = function (_a) {
    var selfUrl = _a.selfUrl;
    var getLabel = useIntl().getLabel;
    var _b = useReviewsContext(), enabled = _b.enabled, dispatch = _b.dispatch;
    var _c = __read(React.useState(false), 2), submitted = _c[0], setSubmitted = _c[1];
    var onSubmit = React.useCallback(function () {
        setSubmitted(true);
        window && window.scrollTo(0, 0);
    }, []);
    React.useEffect(function () {
        if (enabled) {
            dispatch(loadReviewWrite({
                id: REVIEW_WRITE_ID,
                returnUrl: selfUrl,
                onSubmit: onSubmit,
            }));
        }
    }, [enabled, selfUrl, onSubmit]);
    if (!enabled) {
        return null;
    }
    return (React.createElement("div", { className: "review-write", "data-t": "review-write" },
        React.createElement(Container, null,
            !submitted && (React.createElement(Heading, { variant: "small", component: "h1" }, getLabel('reviews.write.title'))),
            React.createElement("div", { id: REVIEW_WRITE_ID }))));
};

export { actions as Actions, PR_SCRIPT_ID, PR_SCRIPT_SRC, ReviewDisplay, ReviewSnippet, ReviewWrite, ReviewsProvider, mapStateToPowerReviewsType, useReviewsContext };
//# sourceMappingURL=index.js.map
